// Vault TanStack Query wrappers — the only place holding vault queries/mutations.
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  checkUploadNames,
  createFolder,
  getDocumentTypeCounts,
  deleteFolderNode,
  deleteRun,
  deleteUpload,
  getArchiveImport,
  getFolderDocumentsById,
  getRootDocuments,
  getFolderTree,
  importArchive,
  linkFolderNodeToProduct,
  unlinkFolderNode,
  renameFolderNode,
  setFolderNodeDomain,
  getDocument,
  getDocumentAudit,
  getDocumentsPage,
  getVaultFolders,
  listDocuments,
  moveFolderNode,
  moveUploadToFolder,
  transitionDocument,
  uploadDocument,
  verifyAudit,
  type ArchiveImportStatus,
  type ListDocumentsParams,
  type TransitionBody,
  type VaultFolderNode,
  type VaultFolderTree,
} from './api';
import type { ListDocumentsPageParams } from './types';
import { VAULT_PAGE_SIZE } from './limits';
import { useMe } from '@/features/auth/hooks';
import { canManageVaultFiles } from './lib/reviewRbac';

const STALE_30S = 30_000;

/** Can the person looking at this CHANGE the vault — upload, move, rename, delete?
 *
 *  The server refuses a viewer on every one of those (backend roles.is_viewer, eleven call
 *  sites), but the list screens never asked: a read-only account saw Upload, New folder,
 *  Rename, Move to… and Delete on every row, pressed one, and got "Viewers cannot edit the
 *  vault." The document page had always asked, through the same helper — so the answer
 *  depended on which screen you were on.
 *
 *  Hide, don't disable: a greyed-out row of buttons is still a row of buttons telling a
 *  reader this is theirs to change.
 *
 *  Asks ``canManageVaultFiles``, NOT ``canEditVaultDoc``. Those answer different questions and
 *  the server gates them differently: file management is refused only to a true read-only
 *  account (``roles.is_viewer`` — none of admin/editor/reviewer), while editing a document's
 *  CONTENT needs ``roles.can_edit`` (editor or admin). Using the content rule here hid Upload,
 *  New folder, Rename, Move and Delete from reviewer-only accounts the server still accepts —
 *  removing access people already had, while trying to stop offering access they never had. */
export function useCanEditVault(): boolean {
  const me = useMe();
  return canManageVaultFiles(me.data?.roles);
}

/** Customer-scoped list — tier: 30s staleTime + refetch on focus. */
export function useVaultDocuments(params: ListDocumentsParams = {}) {
  return useQuery({
    queryKey: ['vault', 'documents', params],
    queryFn: () => listDocuments(params),
    staleTime: STALE_30S,
    refetchOnWindowFocus: true,
  });
}

/** Paginated library (10/page) off the registry — server-side filter/sort/rollup. */
export function useVaultDocumentsPage(params: ListDocumentsPageParams) {
  return useInfiniteQuery({
    queryKey: ['vault', 'documentsPage', params],
    queryFn: ({ pageParam }) => getDocumentsPage({ ...params, offset: pageParam, limit: VAULT_PAGE_SIZE }),
    initialPageParam: 0,
    getNextPageParam: (last) => last.next_offset ?? undefined,
    staleTime: STALE_30S,
  });
}

/**
 * Which of these filenames the destination already holds.
 *
 * A clash does not block the upload — the file lands as "<name>-copy", because losing someone's
 * file is worse than keeping an extra — but showing it BEFORE the upload is what stops a renamed
 * file being a surprise afterwards. Purely advisory: the server dedupes whatever arrives, so a
 * failed check costs a warning, never an upload.
 */
export function useUploadNameCheck(
  filenames: string[],
  dest: { folderId?: number | null; toRoot: boolean },
) {
  return useQuery({
    queryKey: ['vault', 'nameCheck', dest.folderId ?? null, dest.toRoot, filenames],
    queryFn: () => checkUploadNames(filenames, dest),
    enabled: filenames.length > 0,
    // The destination's contents move under us (someone else uploads, a file is deleted), and
    // the answer is only worth anything at the moment the dialog is open.
    staleTime: 0,
    retry: false,
  });
}

export function useVaultDocument(ref: string | null) {
  return useQuery({
    queryKey: ['vault', 'document', ref],
    queryFn: () => {
      if (ref === null) throw new Error('ref is null');
      return getDocument(ref);
    },
    enabled: ref !== null,
    staleTime: STALE_30S,
  });
}

/** Cursor-paginated audit trail (newest first). `fetchNextPage` loads older. */
export function useVaultDocumentAudit(ref: string | null) {
  return useInfiniteQuery({
    queryKey: ['vault', 'audit', ref],
    queryFn: ({ pageParam }) => {
      if (ref === null) throw new Error('ref is null');
      return getDocumentAudit(ref, pageParam);
    },
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: ref !== null,
    staleTime: STALE_30S,
  });
}

/** Admin-only: live verification of the tamper-evident audit chain. */
export function useAuditVerify(enabled: boolean) {
  return useQuery({
    queryKey: ['vault', 'auditVerify'],
    queryFn: verifyAudit,
    enabled,
    staleTime: STALE_30S,
  });
}

export function useDeleteRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ref, runId }: { ref: string; runId: number }) => deleteRun(ref, runId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vault'] }),
  });
}

/** Transition a document through the new state-machine endpoint. */
export function useTransition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ref, body }: { ref: string; body: TransitionBody }) =>
      transitionDocument(ref, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vault'] }),
  });
}

/** Upload a customer's own document into the vault. */
export function useUploadDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: uploadDocument,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vault'] }),
  });
}

/** Upload folders (cards on the vault page). */
export function useVaultFolders() {
  return useQuery({
    queryKey: ['vault', 'folders'],
    queryFn: getVaultFolders,
    staleTime: STALE_30S,
  });
}


/** Create an empty folder (idempotent on case-insensitive sibling name); optional product link
 *  or parent folder (folder-in-a-folder). */
export function useCreateFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { name: string; productId?: number | null; parentId?: number | null }) =>
      createFolder(args.name, args.productId, args.parentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vault'] }),
  });
}

/** The rail's per-kind counts (and the All-files total). Customer-scoped list tier. */
export function useDocumentTypeCounts() {
  return useQuery({
    queryKey: ['vault', 'typeCounts'],
    queryFn: getDocumentTypeCounts,
    staleTime: STALE_30S,
  });
}

/** The customer's nested folder tree (rail data source). */
export function useFolderTree() {
  return useQuery({
    queryKey: ['vault', 'folderTree'],
    queryFn: getFolderTree,
    staleTime: STALE_30S,
  });
}


/** Paginated files in one folder node. A folder is unbounded — an archive import drops a whole
 *  level into one node — so this pages instead of fetching every file and rendering every row.
 *
 *  search/sort are part of the query KEY and go to the server: they change which rows exist, not
 *  just their order, so filtering the loaded page in the browser would hide files on later pages.
 *  Changing either resets to page one, which is what a key change does here. */
export function useFolderDocumentsById(
  folderId: number | null,
  opts: { search?: string; sort?: string; dir?: string } = {},
) {
  const { search = '', sort = 'updated', dir = 'desc' } = opts;
  return useInfiniteQuery({
    queryKey: ['vault', 'folderDocs', folderId, search, sort, dir],
    queryFn: ({ pageParam }) => getFolderDocumentsById(folderId as number, {
      search: search || undefined, sort, dir,
      offset: pageParam as number, limit: VAULT_PAGE_SIZE,
    }),
    initialPageParam: 0,
    getNextPageParam: (last, all) => {
      const loaded = all.reduce((n, p) => n + p.items.length, 0);
      return loaded < last.total ? loaded : undefined;
    },
    enabled: folderId != null,
    staleTime: STALE_30S,
  });
}


/** Keyset-paginated loose files at the vault root (folder_id IS NULL) — the "Vault root" view.
 *  Newest-first; `next_cursor` (an upload id) drives `fetchNextPage`. Only the RootFolderView reads
 *  it, and that mounts solely on the root route, so the fetch is already gated by mount. */
export function useRootDocuments(search?: string) {
  return useInfiniteQuery({
    // The query is part of the key, so paging continues within the filtered set rather than
    // appending pages of a different search to the ones already on screen.
    queryKey: ['vault', 'rootDocs', search ?? ''],
    queryFn: ({ pageParam }) => getRootDocuments({
      cursor: pageParam, limit: VAULT_PAGE_SIZE, search: search || undefined,
    }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    staleTime: STALE_30S,
  });
}

/** Upload a zip/tar for background unzip; returns the pending job. */
export function useImportArchive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => importArchive(file),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vault'] }),
  });
}

/** Folder-node ops by id (work on any tree node, incl. nested). */
export function useRenameFolderNode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { folderId: number; name: string }) => renameFolderNode(a.folderId, a.name),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vault'] }),
  });
}
export function useLinkFolderNode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { folderId: number; productId: number }) => linkFolderNodeToProduct(a.folderId, a.productId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vault'] }),
  });
}
export function useUnlinkFolderNode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (folderId: number) => unlinkFolderNode(folderId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vault'] }),
  });
}
export function useSetFolderNodeDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { folderId: number; domain: string }) => setFolderNodeDomain(a.folderId, a.domain),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vault'] }),
  });
}
const FOLDER_TREE_KEY = ['vault', 'folderTree'] as const;

/** ids to drop from the tree when folder(s) are deleted: the target(s) + every descendant. */
export function folderSubtreeIds(nodes: VaultFolderNode[], rootIds: number[]): Set<number> {
  const childrenOf = new Map<number, number[]>();
  for (const n of nodes) {
    if (n.parent_id == null) continue;
    const arr = childrenOf.get(n.parent_id);
    if (arr) arr.push(n.id);
    else childrenOf.set(n.parent_id, [n.id]);
  }
  const drop = new Set<number>();
  const stack = [...rootIds];
  while (stack.length) {
    const id = stack.pop() as number;
    if (drop.has(id)) continue;
    drop.add(id);
    for (const c of childrenOf.get(id) ?? []) stack.push(c);
  }
  return drop;
}

/** Optimistically drop deleted folder(s) + their subtree from the tree cache so they vanish INSTANTLY
 *  (the sidebar + the folder view both read this tree); returns the snapshot for rollback on error. */
async function optimisticFolderRemove(qc: QueryClient, folderIds: number[]) {
  await qc.cancelQueries({ queryKey: FOLDER_TREE_KEY });
  const prev = qc.getQueryData<VaultFolderTree>(FOLDER_TREE_KEY);
  if (prev) {
    const drop = folderSubtreeIds(prev.nodes, folderIds);
    qc.setQueryData<VaultFolderTree>(FOLDER_TREE_KEY, {
      ...prev,
      nodes: prev.nodes.filter((n) => !drop.has(n.id)),
    });
  }
  return { prev };
}

export function useDeleteFolderNode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (folderId: number) => deleteFolderNode(folderId),
    onMutate: (folderId: number) => optimisticFolderRemove(qc, [folderId]),
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(FOLDER_TREE_KEY, ctx.prev); },
    onSettled: () => qc.invalidateQueries({ queryKey: ['vault'] }),
  });
}
export function useMoveFolderNode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { folderId: number; newParentId: number | null }) => moveFolderNode(a.folderId, a.newParentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vault'] }),
  });
}
export function useMoveUploadToFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { uploadId: number; folderId: number | null }) => moveUploadToFolder(a.uploadId, a.folderId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vault'] }),
  });
}
/** Move several uploads into one folder NODE by id (sequential — one commit each — then refresh). */
export function useBulkMoveUploadsToFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (a: { uploadIds: number[]; folderId: number | null }) => {
      for (const id of a.uploadIds) await moveUploadToFolder(id, a.folderId);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vault'] }),
  });
}
/** Reparent several folder NODES under one destination (sequential — one commit each — then refresh). */
export function useBulkMoveFolders() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (a: { folderIds: number[]; newParentId: number | null }) => {
      for (const id of a.folderIds) await moveFolderNode(id, a.newParentId);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vault'] }),
  });
}
/** Delete several folder NODES — each CASCADES its whole subtree (files + descendant folders);
 *  file rows are soft-deleted and their S3 bytes are RETAINED (no purge — see project retention
 *  decision). Sequential (one commit each), then refresh once. */
export function useBulkDeleteFolders() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (folderIds: number[]) => {
      for (const id of folderIds) await deleteFolderNode(id);
    },
    onMutate: (folderIds: number[]) => optimisticFolderRemove(qc, folderIds),
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(FOLDER_TREE_KEY, ctx.prev); },
    onSettled: () => qc.invalidateQueries({ queryKey: ['vault'] }),
  });
}

/** Poll one archive-import job until it finishes (then stop). */
export function useArchiveImport(id: number | null, opts?: { onDone?: (s: ArchiveImportStatus) => void }) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: ['vault', 'archiveImport', id],
    queryFn: async () => {
      const s = await getArchiveImport(id as number);
      if (s.status === 'done' || s.status === 'failed') {
        qc.invalidateQueries({ queryKey: ['vault'] });  // reveal the newly-imported tree/files
        opts?.onDone?.(s);
      }
      return s;
    },
    enabled: id != null,
    refetchInterval: (q) => {
      const s = q.state.data as ArchiveImportStatus | undefined;
      return s && (s.status === 'done' || s.status === 'failed') ? false : 1500;
    },
  });
}

/** Delete one uploaded document. */
export function useDeleteUpload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (uploadId: number) => deleteUpload(uploadId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vault'] }),
  });
}


/** Delete several uploads (sequential), then refresh once. */
export function useBulkDeleteUploads() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (uploadIds: number[]) => {
      for (const uploadId of uploadIds) await deleteUpload(uploadId);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vault'] }),
  });
}
